import {
  IObservableArray,
  makeAutoObservable,
  observable,
  computed,
} from "mobx";
import {
  EmptyTaskWithTagsAndIllustrationsAndModulesResponseImpl,
  TaskPropApi,
  TaskWithTagsAndIllustrationsAndModulesResponseApi,
} from "../DTO/ServerResponseType/Task/Task";
import { SaveStoreApi } from "./API/SaveStore";
import TaskServerClient from "../ServerClients/TaskServerClient";
import { CheckArgs } from "../utils/checks/CheckArgsWithoutThrow";
import { TaskRequestImpl } from "../DTO/ClientRequest/TaskRequest";
import {
  TaskContentDTO,
  TaskContentType,
} from "../DTO/ClientRequest/TaskContentDTO";
import { TaskIllustrationsStore } from "./TaskIllustrationsStore";
import { TaskDataToCreateResponseApi } from "../DTO/ServerResponseType/Task/TaskDataToCreate";
import { isTaskIdResponseDTO } from "../DTO/ServerResponseType/Task/TaskId";

export class TaskStore implements SaveStoreApi {
  /**
   * Store illustrations
   */
  taskIllustrationsStore: TaskIllustrationsStore;

  /**
   * Task data without content and tags and illustrations
   */
  task: TaskPropApi;

  /**
   * Task content witch should be (set from inputs)
   */
  taskContent: TaskContentDTO;

  /**
   * Any task content without taskContent (set from area)
   */
  taskContentFromArea: string;

  taskTagIds: IObservableArray<string> = observable.array([]);
  withNavigationAfterSaving: boolean = true;

  constructor() {
    this.taskIllustrationsStore = new TaskIllustrationsStore(this);
    this.task = new EmptyTaskWithTagsAndIllustrationsAndModulesResponseImpl();
    this.taskContent = new TaskContentDTO({} as TaskContentType);
    this.taskContentFromArea = "{}";
    makeAutoObservable(this);
  }

  onChangePublicName = (event: React.FormEvent<HTMLInputElement>) => {
    this.task.taskPublicName = event.currentTarget.value;
    this.taskContent.caption = event.currentTarget.value;
  };

  onChangePrivateName = (event: React.FormEvent<HTMLInputElement>) => {
    this.task.taskPrivateName = event.currentTarget.value;
  };

  onChangeCost = (event: React.FormEvent<HTMLInputElement>) => {
    this.task.taskCost = parseInt(event.currentTarget.value);
  };

  onChangeType = (event: React.FormEvent<HTMLSelectElement>) => {
    this.taskContent.type = parseInt(event.currentTarget.value);
  };

  onChangeStatus = (event: React.FormEvent<HTMLSelectElement>) => {
    this.task.taskStatusId = parseInt(event.currentTarget.value);
  };

  onChangeIsMiniGame = (event: React.FormEvent<HTMLInputElement>) => {
    this.taskContent.isMiniGame = event.currentTarget.checked;
  };

  onChangeTypeOfCheck = (event: React.FormEvent<HTMLSelectElement>) => {
    this.taskContent.isAutomaticCheck = event.currentTarget.value === "1";
  };

  onChangeExecutor = (event: React.FormEvent<HTMLSelectElement>) => {
    this.taskContent.executor = parseInt(event.currentTarget.value);
  };

  onChangeContent = (event: React.FormEvent<HTMLTextAreaElement>) => {
    this.taskContentFromArea = event.currentTarget.value;
  };

  onChangeTags = (tagsIds: string[]) => {
    this.taskTagIds.replace(tagsIds);
  };

  @computed get taskContentArea(): string {
    let taskContentAreaFromObject = JSON.parse(this.taskContentFromArea);

    Object.keys(this.taskContent).forEach((key) => {
      delete taskContentAreaFromObject[key];
    });

    return JSON.stringify(taskContentAreaFromObject)
      .split("{")
      .join("{\n\t")
      .split(",")
      .join(",\n\t")
      .split("}")
      .join("\n}");
  }

  @computed get allowIllustrations(): boolean {
    return !CheckArgs.CheckNullOrUndefinedOrEmptyString(this.task.taskId);
  }

  @computed get saveWindowTitle(): string {
    return this.task.taskId === "" ? "Создание задачи" : "Обновление задачи";
  }

  @computed get saveButtonText(): string {
    return this.task.taskId === "" ? "Создать задачу" : "Обновить задачу";
  }

  @computed get saveClickAsync() {
    let taskContentFromAreaObject = {};

    try {
      taskContentFromAreaObject = JSON.parse(this.taskContentFromArea);
    } catch (err) {
      alert(err);
    }

    let req = new TaskRequestImpl({
      ...this.task,
      taskTagIds: this.taskTagIds,
      taskContent: JSON.stringify({
        ...taskContentFromAreaObject,
        ...this.taskContent,
      }),
    });
    this.task.taskModificationTime = new Date();

    let savePromise;
    // TODO: error when save ?
    // catch ?
    if (CheckArgs.CheckNullOrUndefinedOrEmptyString(req.taskId)) {
      this.task.taskCreationTime = new Date();
      savePromise = TaskServerClient.sendToCreateTaskAsync(req).then(
        (taskId: string | null) => {
          if (taskId !== null) {
            this.setId(taskId);
          }

          return taskId;
        }
      );
    } else {
      savePromise = TaskServerClient.sendToUpdateTaskAsync(req.taskId!, req);
    }

    return savePromise.then(async (taskId: string | null) => {
      if (isTaskIdResponseDTO(this.task))
        await TaskServerClient.sendToUpdateTaskStatusAsync(
          this.task.taskId,
          this.task.taskStatusId
        );

      return taskId;
    });
  }

  setTask = (task: TaskWithTagsAndIllustrationsAndModulesResponseApi) => {
    this.task = task;

    this.taskContent = new TaskContentDTO(task.taskContent as TaskContentType);
    this.taskContentFromArea = JSON.stringify(task.taskContent);

    this.taskIllustrationsStore.setIllustrations(task.taskIllustrations);

    this.taskTagIds.replace(
      Array.from(task.taskTags, (taskTag) => taskTag.tag.tagId)
    );
  };

  setId = (taskId: string) => {
    this.task.taskId = taskId;
  };

  loadDataToCreateTaskAsync = async (): Promise<
    TaskDataToCreateResponseApi | never
  > => {
    return TaskServerClient.sendToGetDataToCreateTaskAsync();
  };

  loadTaskAsync = async (
    taskId: string
  ): Promise<TaskWithTagsAndIllustrationsAndModulesResponseApi | never> => {
    return TaskServerClient.sendToGetTaskByTaskIdAsync(taskId);
  };

  loadNewTask =
    async (): Promise<TaskWithTagsAndIllustrationsAndModulesResponseApi> => {
      return new EmptyTaskWithTagsAndIllustrationsAndModulesResponseImpl();
    };
}
